import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

interface CatalogGroup {
  id: string
  name: string
  is_active: boolean
  created_by: string
  updated_by: string
  email_id: string
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  catalog_group_item: any
  discount?: number
}

interface CatalogGroupListProps {
  data: CatalogGroup[]
  isLoading: boolean
  setSelectedRowId: React.Dispatch<React.SetStateAction<string[]>>
  setDiscountValues: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
  selectedRowId: string[]
  discountValues: Record<string, number>
}

function CatalogGroupList({
  data = [],
  isLoading,
  setSelectedRowId,
  setDiscountValues,
  selectedRowId,
  discountValues
}: CatalogGroupListProps) {
  const [globalFilter, setGlobalFilter] = useState('')
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })

  const transformedData = useMemo(() => {
    return data.map((item) => {
      const originalId = item.id
      const stringId =
        typeof originalId === 'string'
          ? originalId
          : JSON.stringify(originalId).replace(/"/g, '')

      return {
        ...item,
        id: stringId,
        discount: discountValues[stringId] || item.discount || 0
      }
    })
  }, [data])

  const handleDiscountChange = useCallback(
    (rowId: string, value: number) => {
      setDiscountValues((prev) => ({
        ...prev,
        [rowId]: value
      }))
    },
    [setDiscountValues]
  )

  const handleRowSelection = useCallback(
    (rowId: string) => {
      setSelectedRowId((prev) => {
        const exist = prev.includes(rowId)
        return exist ? prev.filter((id) => id !== rowId) : [...prev, rowId]
      })
    },
    [setSelectedRowId, selectedRowId]
  )

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: () => null,
        cell: ({ row }) => {
          const rowId = row.original.id
          return (
            <Checkbox
              checked={selectedRowId.includes(rowId)}
              onChange={() => {
                handleRowSelection(rowId)
              }}
            />
          )
        },
        size: 60,
        enableSorting: false
      },
      {
        accessorKey: 'name',
        header: 'Catalog Group Name',
        cell: ({ getValue }) => (
          <Box>
            <Typography variant='body2'>{getValue() as string}</Typography>
          </Box>
        )
      },
      {
        id: 'discount',
        header: 'Discount %',
        cell: ({ row }) => {
          const rowId = row.original.id
          return (
            <TextField
              type='number'
              size='small'
              variant='outlined'
              value={discountValues[rowId] || row.original.discount || ''}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0
                handleDiscountChange(rowId, value)
              }}
              inputProps={{
                min: 0,
                max: 100,
                step: 0.01
              }}
              sx={{
                width: '120px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'grey.300'
                  }
                }
              }}
              placeholder='0.00'
            />
          )
        },
        size: 150,
        enableSorting: false
      }
    ],
    [selectedRowId, discountValues, handleRowSelection, handleDiscountChange]
  )

  const table = useReactTable({
    data: transformedData,
    columns,
    state: {
      globalFilter,
      pagination
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
    debugTable: false
  })

  const { rows } = table.getRowModel()
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    overscan: 5
  })

  const virtualItems = virtualizer.getVirtualItems()

  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '20px'
        }}
      >
        <Stack alignItems='center' spacing={2}>
          <CircularProgress />
          <Typography variant='body2' color='text.secondary'>
            Loading catalog groups...
          </Typography>
        </Stack>
      </Box>
    )
  }

  if (!data.length) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '20px'
        }}
      >
        <Typography variant='body1' color='text.secondary'>
          No catalog groups available
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: '100%', marginTop: '20px' }}>
      {/* Table Container */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Table stickyHeader>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      width: header.getSize(),
                      backgroundColor: 'grey.50',
                      fontWeight: 'bold',
                      borderBottom: '2px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
        </Table>

        {/* Virtualized Table Body */}
        <Box
          ref={parentRef}
          sx={{
            height: '320px',
            overflow: 'auto'
          }}
        >
          {rows.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                No results found
              </Typography>
            </Box>
          ) : (
            <Box
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative'
              }}
            >
              {virtualItems.map((virtualRow) => {
                const row = rows[virtualRow.index]
                return (
                  <Box
                    key={row.id}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`
                    }}
                  >
                    <Table>
                      <TableBody>
                        <TableRow>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              sx={{
                                width: cell.column.getSize(),
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                py: 1
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )
              })}
            </Box>
          )}
        </Box>

        {/* Pagination */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Showing{' '}
            {table.getState().pagination.pageIndex *
              table.getState().pagination.pageSize +
              1}{' '}
            to{' '}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) *
                table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{' '}
            of {table.getFilteredRowModel().rows.length} results
            {selectedRowId && ' • 1 row selected'}
          </Typography>

          <Pagination
            count={table.getPageCount()}
            page={table.getState().pagination.pageIndex + 1}
            onChange={(_, page) => table.setPageIndex(page - 1)}
            color='primary'
            size='small'
          />
        </Box>
      </Paper>
    </Box>
  )
}

export default CatalogGroupList
